import React, { useEffect, useState } from 'react';
import { Users, BarChart3, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { getToken } from '../lib/auth';

const API_BASE = '';

function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(`${API_BASE}${path}`, { ...options, headers });
}

function fmt(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function PocAdminPage() {
  const [regs, setRegs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendEmail, setExtendEmail] = useState('');
  const [extendQuota, setExtendQuota] = useState(20);
  const [msg, setMsg] = useState('');
  const [openId, setOpenId] = useState(null);

  async function load() {
    setLoading(true);
    setMsg('');
    try {
      const [regsRes, statsRes] = await Promise.all([
        apiFetch('/api/poc/admin/registrations'),
        apiFetch('/api/poc/admin/stats'),
      ]);
      const r = await regsRes.json().catch(() => ({}));
      const s = await statsRes.json().catch(() => ({}));
      if (!regsRes.ok) {
        throw new Error(r.error || r.detail || `Registrations failed (${regsRes.status})`);
      }
      if (!statsRes.ok) {
        throw new Error(s.error || s.detail || `Stats failed (${statsRes.status})`);
      }
      setRegs(r.registrations || []);
      setStats(s);
      if (r.healed > 0) {
        setMsg(`Linked ${r.healed} existing POC user(s) into the registration list.`);
      }
    } catch (e) {
      setMsg(e?.message || 'Failed to load POC admin data');
      setRegs([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function extend() {
    setMsg('');
    try {
      const res = await apiFetch('/api/poc/admin/extend-quota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: extendEmail, merchant_quota: extendQuota }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.detail || 'Extend failed');
      }
      setMsg(`Extended ${data.email} to quota ${data.merchant_quota}`);
      await load();
    } catch (e) {
      setMsg(e?.message || 'Extend failed');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" /> POC Registrations
          </h1>
          <p className="text-sm text-gray-500">
            Paylegit MCC (Online) POC — signups, form details, and per-user merchant / MCC usage
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 text-sm border rounded-lg px-3 py-1.5 hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-xl border bg-white p-4">
            <div className="text-xs text-gray-500">Registrations</div>
            <div className="text-2xl font-bold tabular-nums">{stats.total_registrations ?? 0}</div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <div className="text-xs text-gray-500">POC users</div>
            <div className="text-2xl font-bold tabular-nums">{stats.total_poc_users ?? 0}</div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <BarChart3 className="w-3.5 h-3.5" /> Merchants scanned
            </div>
            <div className="text-2xl font-bold tabular-nums">{stats.total_merchant_scans ?? 0}</div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <div className="text-xs text-gray-500">MCC delivered</div>
            <div className="text-2xl font-bold tabular-nums text-emerald-700">
              {stats.total_mcc_delivered ?? 0}
            </div>
          </div>
        </div>
      )}

      {(stats?.daily_signups?.length || stats?.daily_scans?.length) ? (
        <div className="grid md:grid-cols-2 gap-4">
          {!!stats?.daily_signups?.length && (
            <div className="rounded-xl border bg-white p-4">
              <h2 className="text-sm font-semibold mb-2">Signups by day</h2>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                {stats.daily_signups.map((d) => (
                  <li key={d.date} className="flex justify-between gap-2 border-b border-slate-50 py-1">
                    <span className="text-gray-600">{d.date}</span>
                    <span className="font-medium tabular-nums">{d.signups}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!!stats?.daily_scans?.length && (
            <div className="rounded-xl border bg-white p-4">
              <h2 className="text-sm font-semibold mb-2">Merchant scans by day</h2>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                {stats.daily_scans.map((d) => (
                  <li key={d.date} className="flex justify-between gap-2 border-b border-slate-50 py-1">
                    <span className="text-gray-600">{d.date}</span>
                    <span className="font-medium tabular-nums">{d.scans}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {stats && stats.by_country?.length > 0 && (
        <div className="rounded-xl border bg-white p-4">
          <h2 className="text-sm font-semibold mb-2">Signups by country</h2>
          <div className="flex flex-wrap gap-2">
            {stats.by_country.map((c) => (
              <span key={c.signup_country} className="text-xs px-2 py-1 rounded-full bg-slate-100">
                {c.signup_country || '—'}: {c.count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold">Extend merchant quota</h2>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs">
            Email
            <input className="block border rounded px-2 py-1.5 text-sm mt-0.5 min-w-[220px]"
              value={extendEmail} onChange={(e) => setExtendEmail(e.target.value)} />
          </label>
          <label className="text-xs">
            New quota
            <input type="number" min={1} className="block border rounded px-2 py-1.5 text-sm mt-0.5 w-24"
              value={extendQuota} onChange={(e) => setExtendQuota(Number(e.target.value) || 10)} />
          </label>
          <button type="button" onClick={() => void extend()}
            className="bg-blue-600 text-white text-sm px-3 py-1.5 rounded-lg">Apply</button>
        </div>
        {msg && <p className="text-xs text-slate-600">{msg}</p>}
      </div>

      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 w-8" />
              <th className="px-3 py-2">Company / Contact</th>
              <th className="px-3 py-2">Login ID</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Quota</th>
              <th className="px-3 py-2">CSV / job rows</th>
              <th className="px-3 py-2">Merchants ran</th>
              <th className="px-3 py-2">MCC delivered</th>
              <th className="px-3 py-2">First / last scan</th>
              <th className="px-3 py-2">Signed up</th>
              <th className="px-3 py-2">IP / Country</th>
            </tr>
          </thead>
          <tbody>
            {regs.map((r) => {
              const open = openId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t hover:bg-slate-50/80">
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-slate-100"
                        onClick={() => setOpenId(open ? null : r.id)}
                        title="Show merchant runs"
                      >
                        {open
                          ? <ChevronDown className="w-4 h-4 text-slate-500" />
                          : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.company_name || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {r.contact_name || '—'}
                        {r.domain_signup_rank ? (
                          <span className="text-gray-400"> · #{r.domain_signup_rank} on {r.email_domain}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.login_id || r.company_email}</td>
                    <td className="px-3 py-2 text-xs">{r.phone || '—'}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {r.merchants_ran ?? r.merchants_used ?? 0}/{r.merchant_quota}
                      <div className="text-[10px] text-gray-400 font-normal">per email</div>
                    </td>
                    <td className="px-3 py-2 tabular-nums" title="JobItem rows queued under this login (bulk CSV). Can exceed Merchants ran when rows fail, reuse, or lack created_by.">
                      {r.job_items_submitted ?? 0}
                      {(r.jobs_count ?? 0) > 0 && (
                        <div className="text-[10px] text-gray-400">{r.jobs_count} job(s)</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-semibold tabular-nums">{r.merchants_ran ?? 0}</td>
                    <td className="px-3 py-2">
                      <span className="text-emerald-700 font-semibold tabular-nums">{r.mcc_delivered ?? 0}</span>
                      {(r.mcc_pending_or_failed ?? 0) > 0 && (
                        <span className="text-xs text-amber-700 ml-1">
                          ({r.mcc_pending_or_failed} pending/failed)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-gray-600 leading-snug">
                      <div>{fmt(r.first_scan_at)}</div>
                      <div>{fmt(r.last_scan_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.signup_ip || '—'} / {r.signup_country || '—'}
                      {r.postal_code ? ` / ${r.postal_code}` : ''}
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-t bg-slate-50/60">
                      <td colSpan={11} className="px-4 py-3">
                        <div className="text-xs font-semibold text-slate-700 mb-2">
                          Merchant checks by {r.login_id || r.company_email}
                          <span className="font-normal text-gray-500">
                            {' '}— {r.merchants_ran ?? 0} merchant record(s)
                            {(r.job_items_submitted ?? 0) > 0
                              ? `, ${r.job_items_submitted} CSV/job row(s) submitted`
                              : ''}
                          </span>
                          {(r.recent_merchants?.length ?? 0) === 0 && (
                            <span className="font-normal text-gray-400"> — none yet</span>
                          )}
                        </div>
                        {(r.recent_merchants?.length ?? 0) > 0 && (
                          <div className="overflow-x-auto rounded-lg border bg-white">
                            <table className="min-w-full text-xs">
                              <thead className="bg-slate-100 text-gray-500">
                                <tr>
                                  <th className="text-left px-3 py-1.5">When</th>
                                  <th className="text-left px-3 py-1.5">Merchant</th>
                                  <th className="text-left px-3 py-1.5">URL</th>
                                  <th className="text-left px-3 py-1.5">Source</th>
                                  <th className="text-left px-3 py-1.5">MCC</th>
                                  <th className="text-left px-3 py-1.5">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.recent_merchants.map((m) => (
                                  <tr key={m.id} className="border-t">
                                    <td className="px-3 py-1.5 whitespace-nowrap">{fmt(m.scanned_at)}</td>
                                    <td className="px-3 py-1.5 font-medium">{m.name}</td>
                                    <td className="px-3 py-1.5 max-w-[200px] truncate">{m.url || m.city || '—'}</td>
                                    <td className="px-3 py-1.5">{m.source || '—'}</td>
                                    <td className="px-3 py-1.5 font-mono">{m.primary_mcc || '—'}</td>
                                    <td className="px-3 py-1.5">
                                      {m.mcc_delivered ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-700">
                                          <CheckCircle2 className="w-3.5 h-3.5" /> Delivered
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-amber-700">
                                          <XCircle className="w-3.5 h-3.5" /> Pending / no MCC
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {regs.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center text-gray-400">
                  No POC registrations yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
